import React, { useState } from 'react';
import { X, Bug, Cloud, AlertTriangle, Save } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface Props {
  open: boolean;
  onClose: () => void;
  onAlertCreated: () => void;
}

export default function CreatePestAlertModal({ open, onClose, onAlertCreated }: Props) {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    type: 'pest' as 'pest' | 'weather',
    severity: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    title: '',
    titleSt: '',
    message: '',
    messageSt: '',
    location: '',
    pestType: '',
    cropAffected: '',
    weatherCondition: '',
    actionRequired: false,
    expiresInDays: 7
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/api/create-pest-alert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to create alert');
      }

      onAlertCreated();
      onClose();
      setFormData({
        type: 'pest',
        severity: 'medium',
        title: '',
        titleSt: '',
        message: '',
        messageSt: '',
        location: '',
        pestType: '',
        cropAffected: '',
        weatherCondition: '',
        actionRequired: false,
        expiresInDays: 7
      });
    } catch (err: any) {
      setError(err.message || 'Failed to create alert');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
            {language === 'st' ? 'Theha Temoso' : 'Create Alert'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'st' ? 'Mofuta' : 'Type'}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="pest"
                  checked={formData.type === 'pest'}
                  onChange={(e) => setFormData({...formData, type: e.target.value as 'pest'})}
                  className="mr-2"
                />
                <Bug className="w-4 h-4 mr-1" />
                {language === 'st' ? 'Likokoanyana' : 'Pest'}
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="weather"
                  checked={formData.type === 'weather'}
                  onChange={(e) => setFormData({...formData, type: e.target.value as 'weather'})}
                  className="mr-2"
                />
                <Cloud className="w-4 h-4 mr-1" />
                {language === 'st' ? 'Leholimo' : 'Weather'}
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'st' ? 'Boima' : 'Severity'}
            </label>
            <select
              value={formData.severity}
              onChange={(e) => setFormData({...formData, severity: e.target.value as any})}
              className="w-full p-2 border rounded"
            >
              <option value="low">{language === 'st' ? 'Tlase' : 'Low'}</option>
              <option value="medium">{language === 'st' ? 'Mahareng' : 'Medium'}</option>
              <option value="high">{language === 'st' ? 'Phahameng' : 'High'}</option>
              <option value="critical">{language === 'st' ? 'Bohlokoa' : 'Critical'}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'st' ? 'Sehlooho' : 'Title'}
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'st' ? 'Molaetsa' : 'Message'}
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              className="w-full p-2 border rounded h-20"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'st' ? 'Sebaka' : 'Location'}
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="w-full p-2 border rounded"
              placeholder="e.g., Maseru District"
              required
            />
          </div>

          {formData.type === 'pest' && (
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'st' ? 'Lijalo tse Amehileng' : 'Crops Affected'}
              </label>
              <input
                type="text"
                value={formData.cropAffected}
                onChange={(e) => setFormData({...formData, cropAffected: e.target.value})}
                className="w-full p-2 border rounded"
                placeholder="e.g., Maize, Tomato"
              />
            </div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.actionRequired}
              onChange={(e) => setFormData({...formData, actionRequired: e.target.checked})}
              className="mr-2"
            />
            <label className="text-sm">
              {language === 'st' ? 'Ho hlokahala ketso' : 'Action Required'}
            </label>
          </div>

          {error && (
            <div className="p-3 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
            >
              {language === 'st' ? 'Hlakola' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {loading ? (language === 'st' ? 'E boloka...' : 'Creating...') : (language === 'st' ? 'Theha' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}